// ═══════════════════════════════════════════════════════════════
// REVENUE FORECAST REPORT
// Path: src/components/reports/RevenueForecastReport.jsx
//
// "What can we expect this year and where's the risk?"
// Projects annual revenue using prior FY giving as baseline.
// Four sections: KPI summary, segment breakdown, donor table,
// monthly cash flow projection.
// Self-contained — fetches its own data.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  TrendingUp,
  Download,
  Users,
  DollarSign,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowRight,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { getFiscalDates } from "../../utils/fiscalYear";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Map a gift date to the fiscal year it belongs to
const dateToFy = (dateStr, startMonth) => {
  const d = new Date(dateStr + "T12:00:00");
  const month = d.getMonth() + 1;
  return month >= startMonth ? d.getFullYear() : d.getFullYear() - 1;
};

// Compute consecutive fiscal years of giving, counting backwards
const computeConsecutiveYears = (fySet, currentFy) => {
  let startFy = fySet.has(currentFy) ? currentFy : currentFy - 1;
  if (!fySet.has(startFy)) return 0;
  let count = 0;
  while (fySet.has(startFy - count)) count++;
  return count;
};

const SEGMENT_COLORS = {
  Retained: "#10b981",
  Pending: "#f59e0b",
  Lapsed: "#ef4444",
  New: "#3b82f6",
};

export const RevenueForecastReport = () => {
  const {
    palette, members, chapterSettings, canViewGifts, allGifts, navigate,
  } = useApp();

  const fiscalStartMonth = chapterSettings?.fiscalYearStartMonth || 1;
  const fiscal = getFiscalDates(fiscalStartMonth);
  const gifts = allGifts;
  const loading = allGifts.length === 0 && members.length === 0;

  const [sortCol, setSortCol] = useState("projected");
  const [sortDir, setSortDir] = useState("desc");

  // Compute current and prior fiscal years
  const { currentFy, priorFy, prePriorFy } = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const cfy = currentMonth >= fiscalStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    return { currentFy: cfy, priorFy: cfy - 1, prePriorFy: cfy - 2 };
  }, [fiscalStartMonth]);

  // Fiscal year label
  const fyLabel = (fy) => {
    if (fiscalStartMonth === 1) return `CY ${fy}`;
    return `FY ${fy}/${String(fy + 1).slice(2)}`;
  };

  // Build per-member forecast data
  const forecastRows = useMemo(() => {
    // Group gifts by member and fiscal year
    const memberData = {};
    gifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      if (!memberData[mid]) memberData[mid] = { fyTotals: {}, dates: [], fySet: new Set() };
      const fy = dateToFy(g.gift_date, fiscalStartMonth);
      const amount = parseFloat(g.amount) || 0;
      memberData[mid].fyTotals[fy] = (memberData[mid].fyTotals[fy] || 0) + amount;
      memberData[mid].dates.push(g.gift_date);
      memberData[mid].fySet.add(fy);
    });

    return members.map((member) => {
      const data = memberData[member.id];
      const priorFyGiving = data?.fyTotals[priorFy] || 0;
      const currentFyGiving = data?.fyTotals[currentFy] || 0;
      const prePriorFyGiving = data?.fyTotals[prePriorFy] || 0;
      const hasPriorFy = priorFyGiving > 0;
      const hasCurrentFy = currentFyGiving > 0;
      const hasAnyGift = data && data.dates.length > 0;

      // Retention status
      let status;
      if (hasPriorFy && hasCurrentFy) status = "Retained";
      else if (hasPriorFy && !hasCurrentFy) status = "Pending";
      else if (!hasPriorFy && hasCurrentFy) status = "New";
      else if (hasAnyGift) status = "Lapsed";
      else status = null; // no gifts ever — skip

      if (!status) return null;

      // Projected annual gift
      let projected;
      if (status === "Retained") projected = priorFyGiving;
      else if (status === "Pending") projected = priorFyGiving;
      else if (status === "New") projected = currentFyGiving;
      else projected = 0; // Lapsed

      // Gift trend
      let trend = "—";
      if (priorFyGiving > 0 && prePriorFyGiving > 0) {
        if (priorFyGiving > prePriorFyGiving * 1.1) trend = "up";
        else if (priorFyGiving < prePriorFyGiving * 0.9) trend = "down";
        else trend = "steady";
      }

      // Consecutive years
      const consecutiveYears = data ? computeConsecutiveYears(data.fySet, currentFy) : 0;

      // Renewal month — month of most recent gift
      const sortedDates = data ? [...data.dates].sort() : [];
      const lastGiftDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : null;
      const renewalMonth = lastGiftDate ? new Date(lastGiftDate + "T12:00:00").getMonth() : null;

      // Name
      const name = member.donorType === "organization"
        ? member.organization || "Organization"
        : member.donorType === "household"
          ? member.householdName || `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Household"
          : `${member.lastName || ""}, ${member.firstName || ""}`.trim().replace(/^,\s*/, "") || member.credentialedFullName || member.email || "(unnamed)";

      return {
        memberId: member.id,
        name,
        donorType: (member.donorType || "individual").charAt(0).toUpperCase() + (member.donorType || "individual").slice(1),
        priorFyGiving,
        currentFyGiving,
        status,
        consecutiveYears,
        trend,
        renewalMonth,
        projected,
      };
    }).filter(Boolean);
  }, [gifts, members, fiscalStartMonth, currentFy, priorFy, prePriorFy]);

  // Summary KPIs
  const kpis = useMemo(() => {
    const projectedTotal = forecastRows.reduce((s, r) => s + r.projected, 0);
    const collectedTotal = forecastRows.reduce((s, r) => s + r.currentFyGiving, 0);
    const gap = projectedTotal - collectedTotal;
    const atRisk = forecastRows.filter((r) => r.status === "Lapsed").reduce((s, r) => s + r.priorFyGiving, 0);
    return { projectedTotal, collectedTotal, gap: gap > 0 ? gap : 0, atRisk };
  }, [forecastRows]);

  // Segment summary
  const segments = useMemo(() => {
    const segs = {};
    ["Retained", "Pending", "New", "Lapsed"].forEach((s) => {
      segs[s] = { count: 0, priorFy: 0, currentFy: 0, projected: 0 };
    });
    forecastRows.forEach((r) => {
      const seg = segs[r.status];
      if (!seg) return;
      seg.count++;
      seg.priorFy += r.priorFyGiving;
      seg.currentFy += r.currentFyGiving;
      seg.projected += r.projected;
    });
    return segs;
  }, [forecastRows]);

  // Monthly cash flow
  const monthlyFlow = useMemo(() => {
    // Build 12 months of fiscal year
    const months = [];
    for (let i = 0; i < 12; i++) {
      const m = ((fiscalStartMonth - 1 + i) % 12); // 0-indexed month
      months.push({ month: m, label: MONTH_NAMES[m], actual: 0, projected: 0 });
    }

    // Actual: sum current FY gifts by month
    gifts.forEach((g) => {
      const fy = dateToFy(g.gift_date, fiscalStartMonth);
      if (fy !== currentFy) return;
      const gMonth = new Date(g.gift_date + "T12:00:00").getMonth();
      const slot = months.find((ms) => ms.month === gMonth);
      if (slot) slot.actual += parseFloat(g.amount) || 0;
    });

    // Projected: place pending/retained remainder in renewal month
    forecastRows.forEach((r) => {
      if (r.renewalMonth == null) return;
      const slot = months.find((ms) => ms.month === r.renewalMonth);
      if (!slot) return;
      if (r.status === "Pending") {
        slot.projected += r.projected;
      } else if (r.status === "Retained" && r.projected > r.currentFyGiving) {
        slot.projected += r.projected - r.currentFyGiving;
      }
    });

    return months;
  }, [gifts, forecastRows, fiscalStartMonth, currentFy]);

  // Max for bar chart scaling
  const maxMonthly = useMemo(() => {
    return Math.max(...monthlyFlow.map((m) => Math.max(m.actual + m.projected, 1)));
  }, [monthlyFlow]);

  // Sorted donor rows
  const sortedRows = useMemo(() => {
    const rows = [...forecastRows];
    rows.sort((a, b) => {
      let aVal = a[sortCol];
      let bVal = b[sortCol];
      if (aVal == null) aVal = sortDir === "asc" ? Infinity : -Infinity;
      if (bVal == null) bVal = sortDir === "asc" ? Infinity : -Infinity;
      if (typeof aVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return rows;
  }, [forecastRows, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((p) => p === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  // Export CSV
  const handleExport = () => {
    if (sortedRows.length === 0) return;
    const headers = ["Name", "Donor Type", `Prior FY (${fyLabel(priorFy)})`, `Current FY (${fyLabel(currentFy)})`, "Status", "Consec. Years", "Trend", "Renewal Month", "Projected"];
    const rows = sortedRows.map((r) => [
      `"${r.name}"`,
      r.donorType,
      (r.priorFyGiving || 0).toFixed(2),
      (r.currentFyGiving || 0).toFixed(2),
      r.status,
      r.consecutiveYears,
      r.trend === "up" ? "Increasing" : r.trend === "down" ? "Declining" : r.trend === "steady" ? "Steady" : "—",
      r.renewalMonth != null ? MONTH_NAMES[r.renewalMonth] : "—",
      (r.projected || 0).toFixed(2),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-forecast-${fyLabel(currentFy).replace(/[/ ]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Table styles
  const thStyle = (key, align = "left") => ({
    padding: "6px 10px",
    textAlign: align,
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: sortCol === key ? palette.accent1 : palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
    position: "sticky",
    top: 0,
    background: palette.cardBg,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  });

  const tdStyle = (align = "left") => ({
    padding: "5px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
    textAlign: align,
    whiteSpace: "nowrap",
  });

  if (!canViewGifts) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
        Revenue forecasting is available to Admin and Manager roles.
      </div>
    );
  }

  return (
    <div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
          Loading forecast data...
        </div>
      ) : (
        <>
          {/* ── Section 1: KPI Summary ────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <TrendingUp size={ICON_SIZES.sm} style={{ color: "#10b981", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(kpis.projectedTotal)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Projected {fyLabel(currentFy)}
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: "#3b82f6", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(kpis.collectedTotal)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Collected So Far
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: "#f59e0b", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(kpis.gap)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Remaining Gap
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#ef4444", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(kpis.atRisk)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                At-Risk (Lapsed)
              </div>
            </Card>
          </div>

          {/* ── Section 2: Segment Breakdown ──────────────────────── */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
              marginBottom: 12,
            }}>
              <Users size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              Forecast by Retention Segment
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  <th style={{ ...tdStyle(), fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", color: palette.text3, borderBottom: `2px solid ${palette.cardBorder}` }}>Segment</th>
                  <th style={{ ...tdStyle("right"), fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", color: palette.text3, borderBottom: `2px solid ${palette.cardBorder}` }}>Donors</th>
                  <th style={{ ...tdStyle("right"), fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", color: palette.text3, borderBottom: `2px solid ${palette.cardBorder}` }}>{fyLabel(priorFy)}</th>
                  <th style={{ ...tdStyle("right"), fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", color: palette.text3, borderBottom: `2px solid ${palette.cardBorder}` }}>{fyLabel(currentFy)}</th>
                  <th style={{ ...tdStyle("right"), fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", color: palette.text3, borderBottom: `2px solid ${palette.cardBorder}` }}>Projected</th>
                </tr>
              </thead>
              <tbody>
                {["Retained", "Pending", "New", "Lapsed"].map((seg) => {
                  const s = segments[seg];
                  return (
                    <tr key={seg}>
                      <td style={tdStyle()}>
                        <span style={{
                          display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                          background: SEGMENT_COLORS[seg], marginRight: 8, verticalAlign: "middle",
                        }} />
                        <span style={{ fontWeight: 600, color: palette.text1 }}>{seg}</span>
                      </td>
                      <td style={{ ...tdStyle("right"), color: palette.text2 }}>{s.count}</td>
                      <td style={{ ...tdStyle("right"), color: palette.text2 }}>{fmtCurrency(s.priorFy)}</td>
                      <td style={{ ...tdStyle("right"), color: palette.text2 }}>{fmtCurrency(s.currentFy)}</td>
                      <td style={{ ...tdStyle("right"), fontWeight: 700, color: SEGMENT_COLORS[seg] }}>{fmtCurrency(s.projected)}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: `2px solid ${palette.cardBorder}` }}>
                  <td style={{ ...tdStyle(), fontWeight: 700, color: palette.text1 }}>Total</td>
                  <td style={{ ...tdStyle("right"), fontWeight: 700, color: palette.text1 }}>
                    {forecastRows.length}
                  </td>
                  <td style={{ ...tdStyle("right"), fontWeight: 700, color: palette.text1 }}>
                    {fmtCurrency(forecastRows.reduce((s, r) => s + r.priorFyGiving, 0))}
                  </td>
                  <td style={{ ...tdStyle("right"), fontWeight: 700, color: palette.text1 }}>
                    {fmtCurrency(forecastRows.reduce((s, r) => s + r.currentFyGiving, 0))}
                  </td>
                  <td style={{ ...tdStyle("right"), fontWeight: 700, color: palette.accent1 }}>
                    {fmtCurrency(kpis.projectedTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>

          {/* ── Section 3: Monthly Cash Flow ──────────────────────── */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
              marginBottom: 16,
            }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              Monthly Cash Flow — {fyLabel(currentFy)}
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 160, padding: "0 4px" }}>
              {monthlyFlow.map((m) => {
                const total = m.actual + m.projected;
                const barHeight = maxMonthly > 0 ? (total / maxMonthly) * 140 : 0;
                const actualPct = total > 0 ? m.actual / total : 0;
                return (
                  <div
                    key={m.month}
                    style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                    title={`${m.label}: Actual ${fmtCurrency(m.actual)} + Expected ${fmtCurrency(m.projected)}`}
                  >
                    <div style={{ width: "100%", height: barHeight, borderRadius: "4px 4px 0 0", overflow: "hidden", display: "flex", flexDirection: "column-reverse" }}>
                      {/* Actual (bottom, solid) */}
                      <div style={{
                        width: "100%",
                        height: `${actualPct * 100}%`,
                        background: palette.accent1,
                        minHeight: m.actual > 0 ? 2 : 0,
                      }} />
                      {/* Projected (top, lighter) */}
                      <div style={{
                        width: "100%",
                        flex: 1,
                        background: `${palette.accent1}30`,
                        minHeight: m.projected > 0 ? 2 : 0,
                      }} />
                    </div>
                    <span style={{
                      fontSize: "0.55rem", fontWeight: 600, color: palette.text3, fontFamily: FONT_FAMILY,
                    }}>
                      {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{
              display: "flex", gap: 16, justifyContent: "center", marginTop: 10,
              fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY,
            }}>
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, background: palette.accent1, borderRadius: 2, marginRight: 4, verticalAlign: "middle" }} />
                Actual
              </span>
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, background: `${palette.accent1}30`, borderRadius: 2, marginRight: 4, verticalAlign: "middle" }} />
                Expected
              </span>
            </div>
          </Card>

          {/* ── Section 4: Donor-Level Forecast Table ─────────────── */}
          <Card>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12,
            }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
              }}>
                <TrendingUp size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
                Donor-Level Forecast
                <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 400, color: palette.text3 }}>
                  ({sortedRows.length} donors)
                </span>
              </div>
              <ActionButton label="Export CSV" icon={Download} small variant="secondary" onClick={handleExport} />
            </div>

            {sortedRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                No forecast data available.
              </div>
            ) : (
              <div style={{ maxHeight: 600, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                  <thead>
                    <tr>
                      {[
                        { key: "name", label: "Name", align: "left" },
                        { key: "donorType", label: "Type", align: "left" },
                        { key: "priorFyGiving", label: fyLabel(priorFy), align: "right" },
                        { key: "currentFyGiving", label: fyLabel(currentFy), align: "right" },
                        { key: "status", label: "Status", align: "left" },
                        { key: "consecutiveYears", label: "Consec.", align: "right" },
                        { key: "trend", label: "Trend", align: "center" },
                        { key: "renewalMonth", label: "Renewal Mo.", align: "left" },
                        { key: "projected", label: "Projected", align: "right" },
                      ].map((col) => (
                        <th key={col.key} style={thStyle(col.key, col.align)} onClick={() => handleSort(col.key)}>
                          {col.label}
                          {sortCol === col.key && (
                            sortDir === "asc"
                              ? <ArrowUp size={ICON_SIZES.xs} style={{ marginLeft: 2, verticalAlign: "middle" }} />
                              : <ArrowDown size={ICON_SIZES.xs} style={{ marginLeft: 2, verticalAlign: "middle" }} />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr
                        key={row.memberId}
                        style={{ transition: "background 0.15s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td
                          style={{ ...tdStyle(), fontWeight: 600, color: palette.accent1, cursor: "pointer" }}
                          onClick={() => {
                            const member = members.find((m) => m.id === row.memberId);
                            if (member) navigate("memberDetail", member);
                          }}
                        >
                          {row.name}
                        </td>
                        <td style={{ ...tdStyle(), color: palette.text2 }}>{row.donorType}</td>
                        <td style={{ ...tdStyle("right"), color: palette.text2 }}>
                          {row.priorFyGiving > 0 ? fmtCurrency(row.priorFyGiving) : "—"}
                        </td>
                        <td style={{ ...tdStyle("right"), color: palette.text2 }}>
                          {row.currentFyGiving > 0 ? fmtCurrency(row.currentFyGiving) : "—"}
                        </td>
                        <td style={tdStyle()}>
                          <span style={{
                            fontSize: "0.55rem", fontWeight: 600, padding: "2px 8px", borderRadius: 6,
                            background: `${SEGMENT_COLORS[row.status]}15`,
                            color: SEGMENT_COLORS[row.status],
                          }}>
                            {row.status}
                          </span>
                        </td>
                        <td style={{ ...tdStyle("right"), color: palette.text2 }}>{row.consecutiveYears || "—"}</td>
                        <td style={{ ...tdStyle("center") }}>
                          {row.trend === "up" && <ArrowUp size={ICON_SIZES.sm} style={{ color: "#10b981" }} />}
                          {row.trend === "down" && <ArrowDown size={ICON_SIZES.sm} style={{ color: "#ef4444" }} />}
                          {row.trend === "steady" && <ArrowRight size={ICON_SIZES.sm} style={{ color: palette.text3 }} />}
                          {row.trend === "—" && <span style={{ color: palette.text3 }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle(), color: palette.text2 }}>
                          {row.renewalMonth != null ? MONTH_NAMES[row.renewalMonth] : "—"}
                        </td>
                        <td style={{ ...tdStyle("right"), fontWeight: 700, color: row.projected > 0 ? palette.accent1 : palette.text3 }}>
                          {row.projected > 0 ? fmtCurrency(row.projected) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
